import React, { useState } from 'react';
import { MapPin, Phone, Clock, Star, Menu, X, Award, Facebook, ChevronRight, Lock, ArrowRight } from 'lucide-react';

interface PublicWebsiteProps {
    onLoginClick: () => void;
}

const PublicWebsite: React.FC<PublicWebsiteProps> = ({ onLoginClick }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [lightboxImage, setLightboxImage] = useState<string | null>(null);

    const navLinks = [
        { name: 'About Us', href: '#about' },
        { name: 'Products', href: '#products' },
        { name: 'Locations', href: '#locations' },
        { name: 'Reviews', href: '#reviews' },
    ];

    const products = [
        {
            category: "Smoked Meats",
            items: ["Beef Jerky", "Smokies", "Bone-In Ham", "Boneless Ham", "Kolbassi (Plain, Cheese, Holiday)", "Smoked Sausage (Plain, Cheese)", "Smoked Pork Chops", "German Bologna", "Cumberland Gap Bologna"]
        },
        {
            category: "Beef Sticks",
            items: ["Plain", "Regular Hot", "Extra Hot", "El Scorcho", "American Cheese", "Hot Pepper Cheese"]
        },
        {
            category: "Bacon",
            items: ["Thin", "Regular", "Thick", "Pepper", "Garlic"]
        },
        {
            category: "Smoked Cheese",
            items: ["Swiss", "Gouda", "Hot Pepper", "Horseradish", "Colby", "Provolone"]
        },
        {
            category: "Franks",
            items: ["Herb's Franks", "Herb's Franks with American Cheese", "Herb's Franks with Jalapenos and Cheese", "Smith's Natural Casing Hot Dogs", "Smith's Skinless Hot Dogs"]
        },
        {
            category: "Fresh Beef",
            freshFrozen: true,
            items: ["Delmonico Steak", "NY Strip Steak", "Porterhouse Steak", "T-Bone Steak", "Black Nugget Marinated Steaks", "Burgundy Wine Marinated Steaks", "Roasts (Eye of Round, Rump, English Cut, Chuck)", "Ground Beef (Chuck, Round, Stew)"]
        },
        {
            category: "Fresh Pork",
            freshFrozen: true,
            items: ["Baby Back Ribs", "Country Ribs (Bone-in/Boneless)", "City Chicken", "Pork Chops (Bone-in/Boneless/Stuffing)", "Boneless Pork Roast", "Pork Steaks", "Ham Loaf"]
        },
        {
            category: "Fresh Sausages",
            freshFrozen: true,
            items: ["Country Sausage", "Fingerlink Sausage", "Hot Italian Sausage", "Sweet Italian Sausage", "Southern Belle Sausage", "Bulk Sausage", "Bulk Hot Sausage"]
        }
    ];

    const galleryImages = [
        "/cheese.png",
        "/ham.png",
        "/steaks.png",
        "/bologna.png",
        "/pork-roast.png",
        "/smoked-pork-chops.png",
    ];

    const scrollToSection = (id: string) => {
        setMobileMenuOpen(false);
        const element = document.querySelector(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans text-gray-900">

            {/* Easter Banner */}
            <div className="bg-red-900 text-white py-3 px-4 text-center">
                <div className="max-w-7xl mx-auto flex items-center justify-center space-x-3">
                    <span className="text-xl">🐰</span>
                    <p className="text-lg font-bold tracking-wide">
                        Don't forget to order your <span className="text-yellow-400">Easter Hams</span> this year!
                    </p>
                    <span className="text-xl">🐰</span>
                </div>
            </div>

            {/* Quick Contact Bar */}
            <div className="bg-white border-b border-gray-200 py-2 hidden md:block">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end space-x-6 text-sm text-gray-600">
                    <div className="flex items-center">
                        <span className="font-bold text-gray-800 mr-2">Zelienople:</span>
                        <a href="tel:724-452-6772" className="hover:text-red-700 flex items-center">
                            <Phone className="w-3 h-3 mr-1" /> 724-452-6772
                        </a>
                    </div>
                    <div className="w-px bg-gray-300 h-4"></div>
                    <div className="flex items-center">
                        <span className="font-bold text-gray-800 mr-2">Cranberry:</span>
                        <a href="tel:724-779-4372" className="hover:text-red-700 flex items-center">
                            <Phone className="w-3 h-3 mr-1" /> 724-779-4372
                        </a>
                    </div>
                </div>
            </div>

            {/* Navigation */}
            <nav className="sticky top-0 bg-white shadow-md z-40 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-20 items-center">
                        <div className="flex-shrink-0 flex items-center">
                            <h1 className="text-2xl font-bold text-red-900 tracking-tight">HERB BRITTNER'S SMOKEHOUSE</h1>
                        </div>

                        {/* Desktop Nav */}
                        <div className="hidden md:flex space-x-8">
                            {navLinks.map((link) => (
                                <button 
                                    key={link.name} 
                                    onClick={() => scrollToSection(link.href)}
                                    className="text-gray-600 hover:text-red-900 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                                >
                                    {link.name}
                                </button>
                            ))}
                        </div>

                        {/* Mobile menu button */}
                        <div className="flex items-center md:hidden">
                            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-500 hover:text-gray-900 p-2">
                                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-white border-t border-gray-100 absolute w-full shadow-lg">
                        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                            {navLinks.map((link) => (
                                <button 
                                    key={link.name}
                                    onClick={() => scrollToSection(link.href)}
                                    className="block w-full text-left px-3 py-4 text-base font-medium text-gray-700 hover:text-red-900 hover:bg-red-50"
                                >
                                    {link.name}
                                </button>
                            ))}
                            {/* Mobile Contact Info */}
                            <div className="border-t border-gray-100 pt-3 mt-3 px-3 space-y-2 text-sm text-gray-600">
                                <div className="flex items-center justify-between">
                                    <span className="font-bold">Zelienople:</span>
                                    <a href="tel:724-452-6772" className="text-red-700 font-medium">724-452-6772</a>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="font-bold">Cranberry:</span>
                                    <a href="tel:724-779-4372" className="text-red-700 font-medium">724-779-4372</a>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Hero Section */}
            <div className="relative bg-gray-900">
                <div className="absolute inset-0">
                    <img 
                        className="w-full h-full object-cover opacity-50"
                        src="/hero.png"
                        alt="Herb Brittner's Historic Red Barn" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                </div>
                <div className="relative max-w-7xl mx-auto py-24 px-4 sm:py-32 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-900/80 text-red-100 text-xs font-semibold uppercase tracking-wider mb-6 backdrop-blur-sm border border-red-700">
                        <Award className="w-3 h-3 mr-1" /> Family Owned Since the 1940s
                    </div>
                    <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl mb-6 shadow-black drop-shadow-lg">
                        Award Winning <span className="text-red-500">Smoked Meats</span>
                    </h1>
                    <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-100 drop-shadow-md">
                        Serving the Zelienople area for 3 generations. <br />Home of the "Best in the Country" Beef Jerky.
                    </p>
                    <div className="mt-10">
                        <button 
                            onClick={() => scrollToSection('#locations')}
                            className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-700 hover:bg-red-800 md:py-4 md:text-lg transition-all shadow-lg hover:shadow-red-900/30"
                        >
                            Find a Location
                        </button>
                    </div>
                </div>
            </div>

            {/* About & Awards Section */}
            <section id="about" className="py-16 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
                        <div>
                            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl mb-6">
                                A Tradition of Quality
                            </h2>
                            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                Herb is the third generation carrying on the family legacy, using Grandpa Brittner's classic recipes that have been serving the Zelienople area since the 1940s.
                            </p>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                We take pride in our process, ensuring every cut of meat and every stick of jerky meets the high standards our community has come to expect.
                            </p>

                            <div className="bg-red-50 border-l-4 border-red-800 p-6 rounded-r-lg">
                                <h3 className="flex items-center text-xl font-bold text-red-900 mb-3">
                                    <Award className="w-6 h-6 mr-2" />
                                    Award Winning Excellence
                                </h3>
                                <ul className="space-y-2 text-gray-700">
                                    <li className="flex items-start">
                                        <Star className="w-4 h-4 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                                        <span>Beef Jerky voted <strong>Best in the Country</strong> in 1999!</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Star className="w-4 h-4 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                                        <span>2 Grand Championships at the 82nd Annual PAMP Convention</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Star className="w-4 h-4 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                                        <span>Proud members of PAMP & AAMP</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="mt-10 lg:mt-0 relative">
                             <div className="grid grid-cols-2 gap-4">
                                <img className="rounded-lg shadow-lg transform translate-y-4" src="/award.png" alt="Awards" />
                                <img className="rounded-lg shadow-lg transform -translate-y-4" src="/kolbassi.png" alt="Kolbassi" />
                             </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section id="products" className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">Our Products</h2>
                        <p className="mt-4 text-lg text-gray-500">From our famous smoked meats to fresh cuts.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {products.map((cat, idx) => (
                            <div key={idx} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100">
                                <div className="bg-red-900 px-6 py-4 flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-white">{cat.category}</h3>
                                    {cat.freshFrozen && (
                                        <span className="bg-red-800 text-red-100 text-[10px] px-2 py-1 rounded-full uppercase tracking-wide border border-red-700">
                                            +Fresh-Frozen*
                                        </span>
                                    )}
                                </div>
                                <div className="p-6">
                                    <ul className="space-y-2">
                                        {cat.items.map((item, i) => (
                                            <li key={i} className="flex items-start text-gray-700 text-sm">
                                                <ChevronRight className="w-4 h-4 text-red-300 mr-2 flex-shrink-0 mt-0.5" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                    {cat.freshFrozen && (
                                        <p className="mt-4 text-xs text-gray-400 italic border-t border-gray-100 pt-2">
                                            *Available Fresh-Frozen at Cranberry location
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Gallery Preview */}
             <section className="py-8 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                        {galleryImages.map((img, idx) => (
                            <div key={idx} className="relative aspect-square overflow-hidden group cursor-pointer" onClick={() => setLightboxImage(img)}>
                                <img src={img} alt="Gallery" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox */}
            {lightboxImage && (
                <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
                    <button className="absolute top-4 right-4 text-white/70 hover:text-white">
                        <X className="w-8 h-8" />
                    </button>
                    <img src={lightboxImage} alt="Full size" className="max-w-full max-h-[90vh] rounded shadow-2xl" />
                </div>
            )}

            {/* Reviews */}
            <section id="reviews" className="py-16 bg-red-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                     <h2 className="text-3xl font-extrabold mb-12">What Our Customers Say</h2>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <div className="bg-red-800 p-8 rounded-xl relative">
                            <div className="flex justify-center mb-4">
                                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
                            </div>
                            <p className="text-lg italic mb-6">"Herb Brittner's Smokehouse has some of the best meats around... highly recommend a visit."</p>
                            <div className="font-bold uppercase tracking-wide text-sm opacity-75">— War M.</div>
                        </div>
                        <div className="bg-red-800 p-8 rounded-xl relative">
                             <div className="flex justify-center mb-4">
                                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />)}
                            </div>
                            <p className="text-lg italic mb-6">"You won't be disappointed with a single thing that you try! Best there is!"</p>
                            <div className="font-bold uppercase tracking-wide text-sm opacity-75">— Branden S.</div>
                        </div>
                     </div>
                </div>
            </section>

            {/* Locations Section */}
            <section id="locations" className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-extrabold text-gray-900">Visit Us</h2>
                        <p className="mt-2 text-gray-500">We have two convenient locations to serve you.</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Zelienople */}
                        <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-900 mr-4">
                                    <MapPin className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900">Zelienople (Main)</h3>
                                    <p className="text-sm text-gray-500">Processing & Retail</p>
                                </div>
                            </div>

                            <div className="space-y-4 flex-1">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-1" />
                                    <a 
                                        href="https://www.google.com/maps/search/?api=1&query=150+Magill+Rd,+Zelienople,+PA+16063" 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="text-gray-700 hover:text-red-700 hover:underline"
                                    >
                                        150 Magill Rd., Zelienople, PA 16063
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 text-gray-400 mr-3" />
                                    <a href="tel:724-452-6772" className="text-lg font-bold text-gray-900 hover:text-red-700">
                                        724-452-6772
                                    </a>
                                </div>
                                <div className="flex items-start border-t border-gray-200 pt-4 mt-4">
                                    <Clock className="w-5 h-5 text-gray-400 mr-3 mt-1" />
                                    <div className="text-sm text-gray-600 grid grid-cols-[4rem_1fr] gap-x-2 gap-y-1 w-full">
                                        <div className="font-medium">Mon:</div><div>Closed</div>
                                        <div className="font-medium">Tue–Fri:</div><div>9:00 AM – 6:00 PM</div>
                                        <div className="font-medium">Sat:</div><div>9:00 AM – 4:00 PM</div>
                                        <div className="font-medium">Sun:</div><div>Closed</div>
                                    </div>
                                </div>

                            </div>

                            <a 
                                href="https://www.google.com/maps/dir/?api=1&destination=150+Magill+Rd,+Zelienople,+PA+16063" 
                                target="_blank"
                                rel="noreferrer"
                                className="mt-8 w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 text-center flex items-center justify-center transition-colors"
                            >
                                Get Directions <ArrowRight className="w-4 h-4 ml-2" />
                            </a>
                        </div>

                        {/* Cranberry */}
                        <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-900 mr-4">
                                    <MapPin className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-900">Cranberry Twp.</h3>
                                    <p className="text-sm text-gray-500">Herb Brittner's Smoked Meats</p>
                                </div>
                            </div>

                            <div className="space-y-4 flex-1">
                                <div className="flex items-start">
                                    <MapPin className="w-5 h-5 text-gray-400 mr-3 mt-1" />
                                    <a 
                                        href="https://www.google.com/maps/search/?api=1&query=20555+Rt.+19,+Cranberry+Twp.,+PA+16066" 
                                        target="_blank" 
                                        rel="noreferrer"
                                        className="text-gray-700 hover:text-red-700 hover:underline"
                                    >
                                        20555 Rt. 19, Cranberry Twp., PA 16066
                                    </a>
                                </div>
                                <div className="flex items-center">
                                    <Phone className="w-5 h-5 text-gray-400 mr-3" />
                                    <a href="tel:724-779-4372" className="text-lg font-bold text-gray-900 hover:text-red-700">
                                        724-779-4372
                                    </a>
                                </div>
                                <div className="flex items-start border-t border-gray-200 pt-4 mt-4">
                                    <Clock className="w-5 h-5 text-gray-400 mr-3 mt-1" />
                                    <div className="text-sm text-gray-600 grid grid-cols-[5rem_1fr] gap-x-2 gap-y-1 w-full">
                                        <div className="font-medium">Mon-Wed:</div><div>Closed</div>
                                        <div className="font-medium">Thu–Fri:</div><div>11:00 AM – 6:00 PM</div>
                                        <div className="font-medium">Sat:</div><div>10:00 AM – 4:00 PM</div>
                                        <div className="font-medium">Sun:</div><div>Closed</div>
                                    </div>
                                </div>


                                <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                                    *Carries Fresh-Frozen inventory for fresh meat categories.
                                </div>
                            </div>

                             <a 
                                href="https://www.google.com/maps/dir/?api=1&destination=20555+Rt.+19,+Cranberry+Twp.,+PA+16066" 
                                target="_blank"
                                rel="noreferrer"
                                className="mt-8 w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 text-center flex items-center justify-center transition-colors"
                            >
                                Get Directions <ArrowRight className="w-4 h-4 ml-2" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-300 py-12 border-t border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="col-span-1 md:col-span-2">
                            <h3 className="text-xl font-bold text-white mb-4">Herb Brittner's Smokehouse</h3>
                            <p className="text-sm text-gray-400 mb-6 max-w-sm">
                                A family tradition of quality smoked meats and service since the 1940s.
                            </p>
                            <div className="flex space-x-4">
                                <a href="https://www.facebook.com/HerbBrittnerSmokehouseInc" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white transition-colors">
                                    <Facebook className="w-6 h-6" />
                                </a>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Payment Methods</h4>
                            <div className="flex flex-col space-y-2 text-sm text-gray-400">
                                <span>Visa, Mastercard, Discover, American Express, Apple/Google Pay</span>
                                <span className="text-xs text-red-400 mt-1">Note: EBT currently unavailable</span>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Internal</h4>
                            <button 
                                onClick={onLoginClick}
                                className="flex items-center text-sm text-gray-500 hover:text-white transition-colors group"
                            >
                                <Lock className="w-3 h-3 mr-2 group-hover:text-red-500" />
                                Staff Login
                            </button>
                        </div>
                    </div>

                    <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500">
                        <p>&copy; {new Date().getFullYear()} Herb Brittner's Smokehouse, Inc. All rights reserved.</p>
                        <p className="mt-2 md:mt-0">Website by Herb Brittner</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PublicWebsite;
